/**
 * Planetary colour associations for the Lucky Colour Finder.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DOCTRINE, NOT COMPUTATION. Every entry is a traditional association, not a
 * derived value. PENDING: Deepa Saxena to review before this tool is indexed.
 * `colors.test.ts` snapshots the table so a later edit fails CI and forces a
 * fresh review rather than a silent change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const PLANETS = [
  'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu',
] as const;

export type Planet = (typeof PLANETS)[number];

export interface Swatch {
  name: string;
  hex: string;
}

export interface ColorEntry {
  sanskrit: string;
  primary: Swatch;
  secondary: Swatch;
  /** Why this planet carries these colours. */
  rationale: string;
  /** Where the colours are worth using, concretely. */
  uses: string[];
}

export const PLANET_COLORS: Record<Planet, ColorEntry> = {
  Sun: {
    sanskrit: 'Surya',
    primary: { name: 'Deep Saffron', hex: '#E8873A' },
    secondary: { name: 'Ruby Red', hex: '#9B2226' },
    rationale:
      'The Sun governs vitality, authority and the self. Its colours are the warm end of the spectrum — the tones of sunrise rather than of fire.',
    uses: [
      'A saffron or copper accent in a study or workspace',
      'Warm brass and terracotta finishes rather than chrome',
      'Worn on Sundays, or before anything requiring visible confidence',
    ],
  },
  Moon: {
    sanskrit: 'Chandra',
    primary: { name: 'Pearl White', hex: '#F4F1EA' },
    secondary: { name: 'Silver Grey', hex: '#C8CBD0' },
    rationale:
      'The Moon governs the mind, rest and emotional weather. Its colours are soft, reflective and cooling — never harsh.',
    uses: [
      'Bedroom walls, bed linen and anywhere sleep matters',
      'Silver and mother-of-pearl in place of gold',
      'Worn on Mondays, and when you need to settle rather than push',
    ],
  },
  Mars: {
    sanskrit: 'Mangal',
    primary: { name: 'Coral Red', hex: '#C1443C' },
    secondary: { name: 'Burnt Sienna', hex: '#8C4A2F' },
    rationale:
      'Mars governs drive, courage and physical energy. Its reds are earthy and blood-warm rather than bright or shrill.',
    uses: [
      'A single accent wall or textile — never a whole room',
      'Terracotta and unglazed clay in the South or South-East',
      'Worn on Tuesdays, and when something needs starting rather than pondering',
    ],
  },
  Mercury: {
    sanskrit: 'Budha',
    primary: { name: 'Emerald Green', hex: '#2E7D5B' },
    secondary: { name: 'Sage', hex: '#A9BFA8' },
    rationale:
      'Mercury governs intellect, speech and commerce. Its greens are the colours of growth and quick, clear thought.',
    uses: [
      'Studies, home offices and anywhere writing or study happens',
      'Live plants, which serve the same purpose as the colour',
      'Worn on Wednesdays, and before conversations that need clarity',
    ],
  },
  Jupiter: {
    sanskrit: 'Guru',
    primary: { name: 'Turmeric Yellow', hex: '#D9A527' },
    secondary: { name: 'Warm Gold', hex: '#B8962E' },
    rationale:
      'Jupiter governs wisdom, expansion and good counsel. Its yellows are the warm, aged tones of turmeric and old gold.',
    uses: [
      'Entrances, hallways and rooms where guests are received',
      'Brass fittings, and yellow-toned woods over cool greys',
      'Worn on Thursdays, and when seeking or giving advice',
    ],
  },
  Venus: {
    sanskrit: 'Shukra',
    primary: { name: 'Blush', hex: '#E4C7C0' },
    secondary: { name: 'Ivory', hex: '#F2EAD9' },
    rationale:
      'Venus governs beauty, comfort and relationship. Its colours are the soft, refined tones that make a room feel cared for.',
    uses: [
      'Living rooms, dining rooms and shared spaces',
      'Soft furnishings, textiles and anything you touch daily',
      'Worn on Fridays, and for anything to do with partnership',
    ],
  },
  Saturn: {
    sanskrit: 'Shani',
    primary: { name: 'Indigo', hex: '#31465F' },
    secondary: { name: 'Charcoal', hex: '#3A3A3C' },
    rationale:
      'Saturn governs discipline, structure and the long view. Its colours are deep and grounding — used sparingly, they steady a space.',
    uses: [
      'Storage, utility areas and the West or South-West of a room',
      'Iron, dark stone and matte finishes rather than gloss',
      'Worn on Saturdays, and for work requiring patience over flair',
    ],
  },
  Rahu: {
    sanskrit: 'Rahu',
    primary: { name: 'Smoke Grey', hex: '#7E7B78' },
    secondary: { name: 'Deep Umber', hex: '#5A4A3F' },
    rationale:
      'Rahu governs ambition and the unconventional. Its colours are smoky and indistinct, and traditionally used with restraint rather than enthusiasm.',
    uses: [
      'Small accents only — Rahu is not a colour to decorate a room around',
      'Textured, matte surfaces rather than flat blocks of colour',
      'Best balanced by the colours of your own ruling planet',
    ],
  },
  Ketu: {
    sanskrit: 'Ketu',
    primary: { name: 'Muted Ochre', hex: '#B08D57' },
    secondary: { name: 'Stone Grey', hex: '#9A9A93' },
    rationale:
      'Ketu governs detachment and inner focus. Its colours are quiet and earthen, suited to spaces for reflection rather than activity.',
    uses: [
      'Meditation corners and quiet reading spaces',
      'Natural undyed fabrics — linen, jute, raw cotton',
      'Best balanced by the colours of your own ruling planet',
    ],
  },
};

/**
 * Weekday lords (Vara). Index 0 is Sunday, matching Date.getDay().
 *
 * Note: the Vedic day begins at SUNRISE, not midnight, so a birth before dawn
 * belongs to the previous weekday's lord. The tool handles that explicitly
 * rather than silently taking the calendar date.
 */
export const WEEKDAY_LORDS: Planet[] = [
  'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn',
];

/**
 * Vimshottari nakshatra lords, repeating every nine nakshatras from Ashwini.
 * This is the standard Parashari sequence.
 */
const VIMSHOTTARI_CYCLE: Planet[] = [
  'Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury',
];

/** Ruling planet of a nakshatra, by index 0-26. */
export function nakshatraLord(nakshatraIndex: number): Planet {
  return VIMSHOTTARI_CYCLE[nakshatraIndex % 9]!;
}

/** Convention disclosed in the UI alongside every result. */
export const COLOR_CONVENTION =
  'Colours follow the traditional planetary associations of Vedic astrology. The ruling planet is taken either from the weekday of birth (Vara lord) or, when a birth time and place are given, from the birth nakshatra using the standard Vimshottari sequence. We always state which of the two we used.';
